// Currency configuration and conversion utilities

// Exchange rates relative to AED (as of Feb 2026 - approximate rates)
// These should be updated periodically or fetched from an API in production
export const EXCHANGE_RATES = {
    AED: 1,          // Base currency (UAE Dirham)
    USD: 0.27,       // US Dollar
    EUR: 0.25,       // Euro  
    GBP: 0.21,       // British Pound
    SAR: 1.02,       // Saudi Riyal
    KWD: 0.083,      // Kuwaiti Dinar
    BHD: 0.102,      // Bahraini Dinar
    QAR: 0.99,       // Qatari Riyal
    OMR: 0.105,      // Omani Rial
    EGP: 8.35,       // Egyptian Pound
    RUB: 24.5,       // Russian Ruble
    CNY: 1.97,       // Chinese Yuan
    INR: 22.7,       // Indian Rupee
    PKR: 75.6,       // Pakistani Rupee
    PHP: 15.3,       // Philippine Peso
    TRY: 8.8,        // Turkish Lira
    CAD: 0.37,       // Canadian Dollar
    AUD: 0.42,       // Australian Dollar
    JPY: 40.8,       // Japanese Yen
    CHF: 0.24,       // Swiss Franc
};

// Currency symbols and formatting
export const CURRENCY_CONFIG = {
    AED: { symbol: 'AED', name: 'UAE Dirham', flag: '🇦🇪', position: 'after', decimals: 0, locale: 'en-AE' },
    USD: { symbol: '$', name: 'US Dollar', flag: '🇺🇸', position: 'before', decimals: 0, locale: 'en-US' },
    EUR: { symbol: '€', name: 'Euro', flag: '🇪🇺', position: 'before', decimals: 0, locale: 'de-DE' },
    GBP: { symbol: '£', name: 'British Pound', flag: '🇬🇧', position: 'before', decimals: 0, locale: 'en-GB' },
    SAR: { symbol: 'SAR', name: 'Saudi Riyal', flag: '🇸🇦', position: 'after', decimals: 0, locale: 'ar-SA' },
    KWD: { symbol: 'KWD', name: 'Kuwaiti Dinar', flag: '🇰🇼', position: 'after', decimals: 0, locale: 'ar-KW' },
    BHD: { symbol: 'BHD', name: 'Bahraini Dinar', flag: '🇧🇭', position: 'after', decimals: 0, locale: 'ar-BH' },
    QAR: { symbol: 'QAR', name: 'Qatari Riyal', flag: '🇶🇦', position: 'after', decimals: 0, locale: 'ar-QA' },
    OMR: { symbol: 'OMR', name: 'Omani Rial', flag: '🇴🇲', position: 'after', decimals: 0, locale: 'ar-OM' },
    EGP: { symbol: 'EGP', name: 'Egyptian Pound', flag: '🇪🇬', position: 'after', decimals: 0, locale: 'ar-EG' },
    RUB: { symbol: '₽', name: 'Russian Ruble', flag: '🇷🇺', position: 'after', decimals: 0, locale: 'ru-RU' },
    CNY: { symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳', position: 'before', decimals: 0, locale: 'zh-CN' },
    INR: { symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳', position: 'before', decimals: 0, locale: 'en-IN' },
    PKR: { symbol: 'PKR', name: 'Pakistani Rupee', flag: '🇵🇰', position: 'before', decimals: 0, locale: 'en-PK' },
    PHP: { symbol: '₱', name: 'Philippine Peso', flag: '🇵🇭', position: 'before', decimals: 0, locale: 'en-PH' },
    TRY: { symbol: '₺', name: 'Turkish Lira', flag: '🇹🇷', position: 'before', decimals: 0, locale: 'tr-TR' },
    CAD: { symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦', position: 'before', decimals: 0, locale: 'en-CA' },
    AUD: { symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺', position: 'before', decimals: 0, locale: 'en-AU' },
    JPY: { symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵', position: 'before', decimals: 0, locale: 'ja-JP' },
    CHF: { symbol: 'CHF', name: 'Swiss Franc', flag: '🇨🇭', position: 'before', decimals: 0, locale: 'de-CH' },
};

// Map language codes to default currencies
export const LANGUAGE_CURRENCY_MAP = {
    en: 'AED',  // English - show in AED (UAE local currency)
    ar: 'AED',  // Arabic - show in AED
    ru: 'RUB',  // Russian - show in Russian Ruble
    zh: 'CNY',  // Chinese - show in Chinese Yuan
    ur: 'AED',  // Urdu - most Urdu-speaking visitors live in the UAE
};

/**
 * Convert price from AED to target currency
 * @param {number} priceInAED - Price in AED
 * @param {string} targetCurrency - Target currency code (e.g., 'USD', 'RUB')
 * @returns {number} - Converted price
 */
export const convertPrice = (priceInAED, targetCurrency = 'AED') => {
    const rate = EXCHANGE_RATES[targetCurrency] || 1;
    return priceInAED * rate;
};

/**
 * Format price with currency symbol
 * @param {number} price - Price to format (in AED)
 * @param {string} currencyCode - Currency code
 * @param {boolean} abbreviated - Whether to abbreviate large numbers (e.g., 1.5M)
 * @returns {string} - Formatted price string
 */
export const formatPrice = (price, currencyCode = 'AED', abbreviated = true) => {
    if (!price || price <= 0) return null;

    const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.AED;
    const convertedPrice = convertPrice(price, currencyCode);

    let formattedNumber;

    if (abbreviated && convertedPrice >= 1000000) {
        // Format as millions (e.g., 1.5M)
        formattedNumber = (convertedPrice / 1000000).toFixed(1) + 'M';
    } else if (abbreviated && convertedPrice >= 1000) {
        // Format as thousands (e.g., 500K)
        formattedNumber = (convertedPrice / 1000).toFixed(0) + 'K';
    } else {
        // Full number with locale formatting
        formattedNumber = new Intl.NumberFormat(config.locale, {
            maximumFractionDigits: config.decimals
        }).format(convertedPrice);
    }

    // Position the symbol
    if (config.position === 'before') {
        return `${config.symbol}${formattedNumber}`;
    } else {
        return `${formattedNumber} ${config.symbol}`;
    }
};

/**
 * Format price with full number (no abbreviation)
 */
export const formatPriceFull = (price, currencyCode = 'AED') => {
    if (!price || price <= 0) return null;

    const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.AED;
    const convertedPrice = convertPrice(price, currencyCode);

    const formattedNumber = new Intl.NumberFormat(config.locale, {
        maximumFractionDigits: config.decimals
    }).format(convertedPrice);

    if (config.position === 'before') {
        return `${config.symbol} ${formattedNumber}`;
    } else {
        return `${formattedNumber} ${config.symbol}`;
    }
};

/**
 * Get the appropriate currency for the current language
 * @param {string} languageCode - Current language code (e.g., 'en', 'ar', 'ru', 'zh')
 * @returns {string} - Currency code
 */
export const getCurrencyForLanguage = (languageCode) => {
    return LANGUAGE_CURRENCY_MAP[languageCode] || 'AED';
};

/**
 * Format price based on current language
 * @param {number} priceInAED - Price in AED
 * @param {string} languageCode - Current language code
 * @param {boolean} abbreviated - Whether to abbreviate
 * @returns {string} - Formatted price
 */
export const formatPriceByLanguage = (priceInAED, languageCode, abbreviated = true) => {
    const currency = getCurrencyForLanguage(languageCode);
    return formatPrice(priceInAED, currency, abbreviated);
};

/**
 * Get price range options for a given currency
 * Base ranges are in AED, converted to the selected currency
 */
export const getPriceRangeOptions = (currencyCode = 'AED') => {
    const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.AED;
    const rate = EXCHANGE_RATES[currencyCode] || 1;

    // Base ranges in AED - 500K increments
    const baseRanges = [
        { minAED: 0, maxAED: 500000 },
        { minAED: 500000, maxAED: 1000000 },
        { minAED: 1000000, maxAED: 1500000 },
        { minAED: 1500000, maxAED: 2000000 },
        { minAED: 2000000, maxAED: 2500000 },
        { minAED: 2500000, maxAED: 3000000 },
        { minAED: 3000000, maxAED: 3500000 },
        { minAED: 3500000, maxAED: 4000000 },
        { minAED: 4000000, maxAED: 4500000 },
        { minAED: 4500000, maxAED: 5000000 },
        { minAED: 5000000, maxAED: 10000000 },
        { minAED: 10000000, maxAED: 20000000 },
        { minAED: 20000000, maxAED: null },
    ];

    return baseRanges.map(range => {
        const minConverted = Math.round(range.minAED * rate);
        const maxConverted = range.maxAED ? Math.round(range.maxAED * rate) : null;

        const formatNum = (num) => {
            if (num >= 1000000) return (num / 1000000).toFixed(1).replace('.0', '') + 'M';
            if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
            return num.toString();
        };

        let label;
        if (!maxConverted) {
            label = config.position === 'before'
                ? `${config.symbol}${formatNum(minConverted)}+`
                : `${formatNum(minConverted)}+ ${config.symbol}`;
        } else {
            label = config.position === 'before'
                ? `${config.symbol}${formatNum(minConverted)} - ${config.symbol}${formatNum(maxConverted)}`
                : `${formatNum(minConverted)} - ${formatNum(maxConverted)} ${config.symbol}`;
        }

        return {
            value: `${range.minAED}-${range.maxAED || ''}`,
            label,
            minAED: range.minAED,
            maxAED: range.maxAED,
        };
    });
};
